import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { FAQ, FinalCTA } from "../../components/seo/SEOBlocks";
import { Rocket, DollarSign, ShieldCheck, TrendingUp, CheckCircle, ArrowRight } from "lucide-react";

const FAQS = [
  { q: "Will investors take a rapidly-built MVP seriously?", a: "A working product with real user flows always beats a slide deck in an investor conversation. Investors are looking for signal that you can execute — and a live app you can demo answers that question directly. What they don't want is a Figma prototype dressed up as a product." },
  { q: "Do I own the code?", a: "Yes. Once the final invoice is paid you own 100% of the intellectual property — source code, database schema, deployment configuration and all documentation. There is no license fee, no ongoing dependency on us, and no clawback." },
  { q: "Can my future team extend the codebase?", a: "Yes — we build on a mainstream stack (React, FastAPI, MongoDB) that any competent engineering team can pick up. The four-week package includes full per-feature documentation specifically to make the handover painless." },
  { q: "What if I raise before delivery is finished?", a: "That happens. The moment funding is confirmed, we can either finish the current scope as agreed or pause and re-quote the expanded scope on your new runway. There is no early-termination penalty." },
  { q: "Do you sign NDAs?", a: "Every project starts under a standard three-year mutual NDA. You'll see the exact clauses on the Legal page before we ever see your idea." },
  { q: "Which industries do you not build for?", a: "We deliver MVPs suitable for controlled validation. We do not deliver production-hardened systems for regulated deployment — meaning we're a great fit for pre-launch fintech / health / marketplace validation, but the client is responsible for the regulatory hardening required before serving real end-users at scale." },
];

export default function MVPDevelopmentForStartups() {
  return (
    <div className="min-h-screen bg-white" data-testid="page-mvp-for-startups">
      <Navbar />
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-bold uppercase tracking-widest text-zinc-700 mb-6">
            <Rocket size={12} /> For startup founders
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-950 leading-[1.03]">
            MVP development for startups preparing to raise or validate
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Working software before your next investor meeting. Fixed price, senior-developer-reviewed, and structured so your team can pick it up the moment funding lands.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/build" data-testid="cta-hero-build" className="inline-flex items-center justify-center px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              Start a scoping session <ArrowRight size={14} className="ml-2" />
            </Link>
            <Link to="/pricing" data-testid="cta-hero-pricing" className="inline-flex items-center justify-center px-6 py-3 bg-white border border-zinc-200 text-zinc-800 rounded-full text-sm font-semibold hover:border-zinc-400 hover:bg-zinc-50 transition-colors">
              See package pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Why startups pick us</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Runway math that actually works</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: DollarSign, title: "Fixed price, no scope creep", desc: "The price you agree at kick-off is the price on your final invoice. If scope changes we quote it separately — you always see the line item first." },
              { icon: ShieldCheck, title: "You own the IP outright", desc: "Standard three-year NDA up front. Full code ownership on final payment. Nothing to negotiate mid-project." },
              { icon: TrendingUp, title: "Investor-demo ready", desc: "Live software, real user flows, real backend. Fits into a due-diligence data room, not just a pitch deck." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={`startup-value-${title.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}>
                <Icon size={18} className="text-zinc-700 mb-3" />
                <h3 className="font-bold text-zinc-950 mb-2">{title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">What we build</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">The kinds of MVPs that convert investor conversations</h2>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            Not every idea belongs in a rapid MVP. Ideas that <em>do</em> fit have a testable core hypothesis, a first user or paying customer within reach, and a technical footprint we can honestly ship in four weeks or fewer.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "Two-sided marketplaces with a first-hundred-users acquisition plan",
              "SaaS dashboards for a specific vertical with a defined buyer persona",
              "Consumer utilities validating a paid-conversion assumption",
              "B2B tools replacing a spreadsheet or manual workflow",
              "Analytics or reporting layers over an existing data source",
              "AI-augmented workflow tools with a clear before/after time-savings claim",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3 bg-zinc-50 rounded-xl p-4 border border-zinc-100">
                <CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-700 leading-relaxed">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-zinc-600 leading-relaxed">
            If your idea doesn't sit in one of these shapes, that's not a rejection — <Link to="/build" className="underline font-semibold text-zinc-900">start a scope</Link> anyway. The scoping call is where we tell you honestly whether the four-week timeline is realistic.
          </p>
        </div>
      </section>

      <FAQ items={FAQS} title="Founder questions we get most often" />
      <FinalCTA heading="Bring us your hardest question" subheading="If your MVP can be scoped in an afternoon, we can quote it. If it can't, we'll tell you." />
      <Footer />
    </div>
  );
}
